import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { getCurrencyFormat } from '../../../global/helpers/format-helpers';
import { replaceAll } from '../../../global/helpers/string-helpers';
import { YesNoIndicators } from '../../../global/models/api-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { Life, LifeData, LifePension, MyCpgPodsSettings, MyCpgPodsTypes } from '../mycpg-pod-models';
import { PodGridContent } from './pod-grid-content';
import { PodGridContentItem } from './pod-grid-content-item';
import { PodGridItem } from './pod-grid-item';
import { PodGridItemHeader } from './pod-grid-item-header';


interface Props {
	settings: MyCpgPodsSettings;
}
export function LifePlanPod(props: Props) {

	const { settings } = props;
	const lifePlan = useSelector<TcsmStates, LifeData>(state => state?.mycpgPodsState?.pods?.life_data);
	const hasLifePlan = lifePlan?.data?.length > 0 || lifePlan?.pension_life_data?.length > 0;
	const routeMatch = useRouteMatch();
	const lifeData = lifePlan?.data?.slice(0, 2);
	const pensionLifePlan = lifePlan?.pension_life_data?.slice(0, 2 - lifeData?.length);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState.clientGuid);

	const totalCount = lifePlan?.data?.length + lifePlan?.pension_life_data?.length;

	if (lifePlan?.show_pod === YesNoIndicators.No) {
		return null;
	}

	const renderLifePlan = (l: Life | LifePension, pensionLife?: boolean) => {

		if (settings?.enableFtj && l?.ftj_link !== undefined)
			return (
				<>
					<p className='h3-styled'>{replaceAll(settings?.insuranceLabel, '{#insurance}', l?.type)}</p>
					<p className='grey-caption'>{replaceAll(settings?.policyNumberLabel, '{policy#}', l?.policy_id)}</p>
					<p><ExternalLink linkUrl={replaceAll(settings?.lifePlanFtjRedirect, '{guid}', guid)} linkText={settings?.lifePlanFtjLinkText} /></p>
				</>);

		if (l?.product_type === 'I' && !settings?.enableFtj)
			return (<ContentSpacer>
				<WysiwygWithReactLink content={settings?.newIndividualLifePlanPodMessage} />
			</ContentSpacer>);


		return (
			pensionLife ?
				<>
					<p className='h3-styled'>{getCurrencyFormat(l?.coverage_amount)}</p>
					<p className='grey-caption'>{replaceAll(settings?.benefitCoverageAmountLabel, '{PlanType}', l?.policy_name)}</p>
				</>
				:
				<>
					<p className='h3-styled'>{getCurrencyFormat(l?.coverage_amount)}</p>
					<p className='grey-caption'>{replaceAll(settings?.benefitCoverageAmountLabel, '{PlanType}', l.type)}</p>
				</>);
	};

	const renderContent = () => {

		if (!hasLifePlan) {
			return (
				<PodGridContentItem>
					<div dangerouslySetInnerHTML={{ __html: settings?.noLifePlanText }} />
				</PodGridContentItem>
			);
		}

		if (lifeData?.length === 2) {
			return lifeData?.map(l =>
				<PodGridContentItem key={l.policy_id}>
					{
						renderLifePlan(l)
					}
				</PodGridContentItem>,
			);
		}


		return (
			<>
				{
					pensionLifePlan?.map(pl =>
						<PodGridContentItem key={pl?.policy_name}>
							{
								renderLifePlan(pl, true)
							}
						</PodGridContentItem>,
					)
				}
				{
					lifeData?.map(l =>
						<PodGridContentItem key={l.policy_id}>
							{
								renderLifePlan(l)
							}
						</PodGridContentItem>,
					)
				}

			</>
		);

	};

	return (
		<PodGridItem>
			<PodGridItemHeader title={`${settings?.lifePlansTitle} (${totalCount})`}>
				<Link to={`${routeMatch?.path}?type=${MyCpgPodsTypes.LIFE}`}>{!hasLifePlan ? settings?.learnMoreText : settings?.seeMoreLinkText}</Link>
			</PodGridItemHeader>
			<PodGridContent>
				{renderContent()}
				<WysiwygWithReactLink content={settings.lifePlanPodFooterContent} />
			</PodGridContent>
		</PodGridItem>
	);

}

